import React from 'react';
import { Link } from 'react-router-dom';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function PrivacyPolicyPage() {
  const lastUpdated = "April 9, 2026";

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="privacy-policy-title">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#999999] mb-10">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-[#CCCCCC] text-sm leading-relaxed">
          <Section title="1. Introduction">
            <p>
              Divispend ("we," "our," or "us") operates the Divispend platform, a payment and commerce service that enables users to send, receive, and manage money as well as list and purchase products and services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
            </p>
            <p>
              By using Divispend, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this policy, please do not access or use our services.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p className="font-semibold text-white mb-2">Personal Information</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Name, email address, and phone number</li>
              <li>Payment details (processed securely via Stripe and PayPal)</li>
              <li>Bank account or payout details for withdrawals</li>
              <li>Profile information including username, bio, and avatar</li>
              <li>Business information for seller accounts</li>
            </ul>
            <p className="font-semibold text-white mb-2 mt-4">Automatically Collected Information</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Device type, browser type, and operating system</li>
              <li>IP address and approximate location</li>
              <li>Usage data: pages visited, features used, time spent</li>
              <li>Transaction history and wallet activity</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul className="list-disc pl-5 space-y-1">
              <li>To create, maintain, and secure your account</li>
              <li>To process payments, transfers, and withdrawals</li>
              <li>To send transaction receipts and notifications</li>
              <li>To detect and prevent fraud, unauthorized access, and abuse</li>
              <li>To improve and personalize our services</li>
              <li>To communicate updates, promotions, and support messages</li>
              <li>To comply with legal obligations and regulatory requirements</li>
            </ul>
          </Section>

          <Section title="4. Third-Party Services">
            <p>We share information with trusted third-party service providers to operate our platform:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong className="text-white">Stripe</strong> — Payment processing, Stripe Connect for payouts. Subject to <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#22C55E] hover:underline">Stripe's Privacy Policy</a>.</li>
              <li><strong className="text-white">PayPal</strong> — Alternative payment processing. Subject to <a href="https://www.paypal.com/us/legalhub/privacy-full" target="_blank" rel="noopener noreferrer" className="text-[#22C55E] hover:underline">PayPal's Privacy Policy</a>.</li>
              <li><strong className="text-white">Resend</strong> — Transactional email delivery.</li>
              <li><strong className="text-white">Google</strong> — Social login authentication.</li>
            </ul>
            <p className="mt-2">We do not sell your personal information to third parties.</p>
          </Section>

          <Section title="5. Data Security">
            <p>
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Encrypted data transmission (HTTPS/TLS 1.2+)</li>
              <li><strong className="text-white">Field-level encryption</strong> for sensitive PII (phone, address, KYC documents) using Fernet (AES-128-CBC + HMAC-SHA256)</li>
              <li>Hashed and salted password storage (bcrypt, cost factor 12)</li>
              <li><strong className="text-white">Tamper-evident audit chain</strong> for privileged actions — each entry hash-linked to the previous, with admin verification on demand</li>
              <li>Header-only auth tokens stored in localStorage; <strong className="text-white">no cross-site cookies</strong></li>
              <li>Two-Factor Authentication (TOTP) for account security</li>
              <li>Fraud detection and monitoring systems</li>
              <li>SOC 2 Type II observation window in progress — see our <Link to="/trust" className="text-[#22C55E] hover:underline">Trust &amp; Security page</Link> for the full controls inventory</li>
              <li>Quarterly access reviews and incident-response drills</li>
            </ul>
            <p className="mt-2">While we strive to protect your data, no method of electronic transmission or storage is 100% secure. We notify affected users of any confirmed data incident within the timeframes required by applicable law.</p>
          </Section>

          <Section title="6. Cookies & Local Storage">
            <p>
              Divispend uses local storage (not cookies) to maintain your authentication session. We do not use third-party tracking cookies. Service workers may cache static assets for offline functionality as part of our Progressive Web App features.
            </p>
          </Section>

          <Section title="7. Your Rights (GDPR & CCPA)">
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong className="text-white">Access</strong> — Request a copy of the personal data we hold about you</li>
              <li><strong className="text-white">Rectification</strong> — Request correction of inaccurate data</li>
              <li><strong className="text-white">Erasure</strong> — Request deletion of your personal data</li>
              <li><strong className="text-white">Portability</strong> — Request an export of your data in a portable format</li>
              <li><strong className="text-white">Restriction</strong> — Request that we limit processing of your data</li>
              <li><strong className="text-white">Objection</strong> — Object to the processing of your data</li>
              <li><strong className="text-white">Withdraw Consent</strong> — Withdraw consent for data processing at any time</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, visit your <Link to="/settings/privacy" className="text-[#22C55E] hover:underline">Privacy Settings</Link> page or contact us at the email below.
            </p>
          </Section>

          <Section title="8. Data Retention">
            <p>
              We retain your personal data for as long as your account is active or as needed to provide services. Transaction records are retained for a minimum of 7 years to comply with financial regulations. You may request deletion of your account and associated data at any time through your Privacy Settings.
            </p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>
              Divispend is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we discover that a child under 18 has provided us with personal data, we will delete such information promptly.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of our services after changes are posted constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>
              If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us at:
            </p>
            <div className="mt-3 p-4 bg-[#1A1A1A] border border-[#333333] rounded-sm">
              <p className="text-white font-semibold">Divispend Support</p>
              <p className="text-[#999999]">Email: privacy@divispend.com</p>
            </div>
          </Section>
        </div>
      </main>

      <footer className="border-t border-[#333333] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 w-auto" />
          </Link>
          <p className="text-sm text-[#999999]">
            &copy; 2026 Divispend. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
